<template>
  <article class="card card--resource">
    <div class="card__inner">
      <MediaCardImage
        :card-url="pageLink"
        :card-url-title="title"
        :max-width="600"
        :media="media"
        class="media--card"
        fit="contain"
      />
      <div class="card__content">
        <div>
          <TagsList
            :audiences="audiences"
            :sorting="isDefaultTagsSorting"
            :topics="topics"
            :use-audience-as-primary="isPrimaryAudience"
          />
          <BlockLink v-if="title" :label="title" :link="pageLink">
            <h3 class="card__title">
              {{ title }}
            </h3>
          </BlockLink>
          <p v-if="introduction" class="card__intro">
            {{ trimText(introduction, 120) }}
          </p>
        </div>
        <div class="card__meta">
          <div
            v-if="states"
            aria-label="States and Territories this resource relates to"
            class="card__states flex"
          >
            <span v-for="state in states" :key="state">
              {{ state }}
            </span>
          </div>
          <div class="card__meta__group">
            <div v-if="resourceType" aria-label="Type of resource" class="item">
              <i class="icon">
                <Icon name="fa6-regular:file" />
              </i>
              <span class="capitalize">
                {{ resourceType }}
              </span>
            </div>
            <div v-if="organisation && organisationLink" class="link">
              <a
                :href="organisationLink"
                aria-label="Organisation related to this Resource - opens in new tab"
                class="resource__card-link"
                rel="external"
                target="_blank"
              >
                <i class="icon">
                  <Icon name="fa6-solid:link" />
                </i>
                <span>
                  {{ organisation }}
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { trimText } from '#imports'
import type { ResourceStoryblok } from '~/types/storyblok'
const props = defineProps<{
  blok?: ResourceStoryblok
  customStory?: StoryContentBlok
}>()

const storyContent = computed(() => props.blok?.content)

const {
  customLink,
  introduction,
  title,
  audiences,
  topics,
  isDefaultTagsSorting,
  isPrimaryAudience,
  fallbackImage,
  states,
  resourceType,
} = useStoryContent({ ...toRefs(props), storyContent })
const media = computed(() => props.blok?.content?.image || fallbackImage.value)
const organisation = computed(() => props.blok?.content?.organisation)
const organisationLink = computed(() => props.blok?.content?.link?.url)
const pageLink = computed(
  () => customLink.value || prependSlashToInternalUrl(props.blok?.full_slug),
)
</script>

<style lang="less" scoped>
.card.card--resource {
  .card__inner {
    .media {
      padding: @gutter-xs;
      background: @color-fill-pale;
    }

    .card__intro {
      .hidden();

      @media @tablet {
        .block();
      }
    }
  }

  &:first-child {
    .card__inner {
      .card__content {
        border-top: solid @1px @color-fill-divide;

        @media @tablet {
          border: none;
        }
      }
    }
  }

  .card__meta {
    .font-medium();
    .text-body();

    .card__states,
    .link {
      .hidden();

      @media @tablet {
        .flex();
      }
    }
  }
}
</style>
